import { create } from 'zustand';
import type { Board, Stroke, BBox } from '@blackboard/shared';
import { StrokeIndex } from '../canvas/spatialIndex';

/**
 * Incremental mutation log for the static-layer renderer (CanvasStage RAF).
 * addStroke/removeStroke/bulkLoad append records here; the render loop splices
 * them each frame and paints only what changed — no full repaints, no canvas
 * remounts. Consumers: exactly one (CanvasStage).
 */
export type BoardMutation =
  | { kind: 'add'; stroke: Stroke }
  | { kind: 'adds'; strokes: Stroke[] }
  | { kind: 'remove'; id: number; bbox: BBox }
  | { kind: 'bulk' };

interface BoardState {
  board: Board | null;
  index: StrokeIndex;
  indexVersion: number; // bump to signal consumers to re-render
  lastStrokeId: number;
  mutations: BoardMutation[];
  setBoard: (b: Board) => void;
  addStroke: (s: Stroke) => void;
  removeStroke: (id: number) => void;
  bulkLoad: (ss: Stroke[]) => void;
  reset: () => void;
}

function bump(s: BoardState): Partial<BoardState> {
  return { indexVersion: s.indexVersion + 1 };
}

export const useBoard = create<BoardState>((set) => ({
  board: null,
  index: new StrokeIndex(),
  indexVersion: 0,
  lastStrokeId: 0,
  mutations: [],
  setBoard: (board) =>
    set((s) => {
      s.index.clear();
      return { board, indexVersion: s.indexVersion + 1, lastStrokeId: 0, mutations: [{ kind: 'bulk' }] };
    }),
  addStroke: (stroke) =>
    set((s) => {
      s.index.insert(stroke);
      return {
        ...bump(s),
        lastStrokeId: Math.max(s.lastStrokeId, stroke.id),
        mutations: [...s.mutations, { kind: 'add', stroke }],
      };
    }),
  removeStroke: (id) =>
    set((s) => {
      const bbox = s.index.getById(id)?.bbox;
      s.index.remove(id);
      if (!bbox) return bump(s);
      return { ...bump(s), mutations: [...s.mutations, { kind: 'remove', id, bbox }] };
    }),
  bulkLoad: (ss) =>
    set((s) => {
      // 只把"新笔画"增量入渲染队列——平移/缩放加载新瓦片时不再整屏重绘
      // （每次拖动停下 200ms 后都 full repaint 一次，正是"拖动一卡一卡"的残余根因）。
      const fresh = ss.filter((x) => !s.index.getById(x.id));
      if (fresh.length === 0) return s;
      s.index.bulkLoad(ss);
      const maxId = ss.reduce((m, x) => Math.max(m, x.id), s.lastStrokeId);
      return {
        ...bump(s),
        lastStrokeId: maxId,
        mutations: [...s.mutations, { kind: 'adds' as const, strokes: fresh }],
      };
    }),
  reset: () =>
    set((s) => {
      s.index.clear();
      return { ...bump(s), lastStrokeId: 0, mutations: [{ kind: 'bulk' }] };
    }),
}));
